import { observer } from 'mobx-react-lite'
import { BreakoutTable } from 'components/design-system/BreakoutTable'
import { BreakoutTableColumnHeader } from 'components/design-system/BreakoutTableColumnHeader'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { BreakoutTextArea } from 'components/design-system/BreakoutTextArea'
import { BreakoutCheckbox } from 'components/design-system/BreakoutCheckbox'
import { useTranslationTyped } from 'i18n/i18n'
import { useState, useCallback, useEffect } from 'react'
import { peerReviewScoreColor } from '@breakoutlearning/firebase-repository/sources-of-truth/peerReviewScores'
import type { SectionAssignmentPeerReviewQuestion } from '@breakoutlearning/firebase-repository/models/SectionAssignmentPeerReviewQuestion'
import classNames from 'classnames'
import { PlusIcon } from 'components/icons/Plus'
import {
  PeerReviewMeaningfulScore,
  peerReviewScoreProperties,
  peerReviewMeaningfulScoreFromDBValue,
} from '@breakoutlearning/firebase-repository/models/RoomStateUPRPeerReview'
import { RequireReasonForNADialog } from './RequireReasonForNADialog'
import { TruncatingText } from 'components/TruncatingText'
import type { RevieweeForPeerReviewGrid } from '@breakoutlearning/firebase-repository/cubits/StudentAssignment/StudentPeerReviewController'

export const PeerReviewGrid = observer(function PeerReviewGrid(
  props: {
    peerReviewQuestions: SectionAssignmentPeerReviewQuestion[]
    reviewees: RevieweeForPeerReviewGrid[]
  } & (
    | {
        readOnly: true
      }
    | {
        readOnly: false
        stagedReviews: Map<string, Map<string, PeerReviewMeaningfulScore>>
        onScoreChange: (
          revieweeUserId: string,
          questionId: string,
          scoreOption: PeerReviewMeaningfulScore
        ) => void
        stagedNotes: Map<string, string>
        onNoteChange: (revieweeUserId: string, note: string) => void
        stagedSharing: Map<string, boolean>
        onSharingChange: (revieweeUserId: string, shared: boolean) => void
      }
  )
) {
  const { peerReviewQuestions, reviewees, readOnly } = props
  const { tt } = useTranslationTyped()

  const getScore = useCallback(
    (revieweeUserId: string, questionId: string) => {
      if (readOnly) {
        const reviewee = reviewees.find((r) => r.user.id === revieweeUserId)
        const dbValue = reviewee?.reviewData.review.get(questionId)

        if (dbValue !== undefined) {
          return peerReviewMeaningfulScoreFromDBValue(dbValue)
        }
      } else {
        return props.stagedReviews.get(revieweeUserId)?.get(questionId)
      }
    },
    [readOnly, props, reviewees]
  )

  const getNote = useCallback(
    (revieweeUserId: string) => {
      if (readOnly) {
        const reviewee = reviewees.find((r) => r.user.id === revieweeUserId)
        return reviewee?.reviewData.note
      } else {
        const staged = props.stagedNotes.get(revieweeUserId)
        if (staged) {
          return staged
        }
      }
    },
    [readOnly, props, reviewees]
  )

  const getSharing = useCallback(
    (revieweeUserId: string) => {
      if (readOnly) {
        const reviewee = reviewees.find((r) => r.user.id === revieweeUserId)
        return reviewee?.reviewData.sharedWithPeer ?? false
      } else {
        const staged = props.stagedSharing.get(revieweeUserId)
        if (staged !== undefined) {
          return staged
        }
        // Fall back to existing data if not staged
        const reviewee = reviewees.find((r) => r.user.id === revieweeUserId)
        return reviewee?.reviewData.sharedWithPeer ?? false
      }
    },
    [readOnly, props, reviewees]
  )

  return (
    <div className="relative flex flex-col">
      <BreakoutTable>
        <thead className="sticky top-0 bg-surface-bright text-body-small">
          <tr>
            <BreakoutTableColumnHeader
              label={tt.instructor_assignment.quiz_poll_results.header_student()}
              className="w-40 py-3"
            />
            {peerReviewQuestions.map((question) => (
              <BreakoutTableColumnHeader
                key={question.id}
                label={question.data.prQuestion}
                tooltip={question.data.prDescription}
                alignment="center"
                className="!px-4 py-3"
                tooltipClassName="max-w-[300px] whitespace-pre-line"
                labelClassName="!text-wrap"
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {reviewees.map((reviewee) => {
            let leftContent: React.ReactNode

            if (readOnly) {
              const note = getNote(reviewee.user.id)
              if (note) {
                leftContent = (
                  <BreakoutTooltip
                    className="max-w-80"
                    content={note}
                    enabled={note.length > 100}
                  >
                    <div className="max-w-60 text-body-small text-on-surface-var">
                      {note.length > 100 ? `${note.slice(0, 100)}...` : note}
                    </div>
                  </BreakoutTooltip>
                )
              }
            } else if (reviewee.isCurrentUser) {
              leftContent = (
                <div
                  data-testid="peer-review-self-review-row"
                  className="text-body-small font-bold text-on-surface-var"
                >
                  {tt.student_assignment.peer_review_dialog.self_review()}
                </div>
              )
            } else {
              leftContent = (
                <NoteEditor
                  revieweeUserId={reviewee.user.id}
                  note={getNote(reviewee.user.id)}
                  onNoteChange={props.onNoteChange}
                  isShared={getSharing(reviewee.user.id)}
                  onSharingChange={props.onSharingChange}
                />
              )
            }

            return (
              <tr
                key={reviewee.user.id}
                className={classNames('!border-b-0', {
                  'bg-surface-light': true,
                  'bg-surface-dim': reviewee.isCurrentUser,
                })}
              >
                <td className="max-w-40 rounded-bl-xl rounded-tl-xl !border-b-0 px-5 py-2">
                  <div className="flex min-h-8 flex-col gap-1">
                    <TruncatingText
                      className="text-label-medium font-bold text-on-surface"
                      text={reviewee.user.fullName}
                    />
                    {leftContent}
                  </div>
                </td>
                {peerReviewQuestions.map((question, index) => {
                  const scoreOption = getScore(reviewee.user.id, question.id)
                  const isLastCell = index === peerReviewQuestions.length - 1
                  return (
                    <td
                      key={question.id}
                      className={classNames(
                        '!border-b-0 !py-0 px-5 text-center',
                        isLastCell && 'rounded-br-xl rounded-tr-xl'
                      )}
                    >
                      {readOnly ? (
                        <ReadOnlyScoreDisplay scoreOption={scoreOption} />
                      ) : (
                        <EditableScoreCell
                          scoreOption={scoreOption}
                          revieweeUserId={reviewee.user.id}
                          revieweeName={reviewee.user.fullName}
                          existingNote={getNote(reviewee.user.id)}
                          currentQuestionId={question.id}
                          allQuestionIds={peerReviewQuestions.map((q) => q.id)}
                          onScoreChangeForQuestion={(
                            questionId,
                            newScoreOption
                          ) =>
                            props.onScoreChange(
                              reviewee.user.id,
                              questionId,
                              newScoreOption
                            )
                          }
                          onNoteChange={props.onNoteChange}
                          isSelfReview={reviewee.isCurrentUser}
                        />
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </BreakoutTable>
    </div>
  )
})

const ReadOnlyScoreDisplay = ({
  scoreOption,
}: {
  scoreOption: PeerReviewMeaningfulScore | undefined
}) => {
  if (scoreOption === undefined) {
    return <div className="text-body-medium text-on-surface-var">—</div>
  }

  const numericalValue = peerReviewScoreProperties[scoreOption].numericalValue
  const colorClasses = peerReviewScoreColor(numericalValue)

  return (
    <div
      data-testid="peer-review-readonly-score"
      data-score={numericalValue}
      className="flex items-center justify-center gap-1"
    >
      <div
        className={classNames(`h-5 w-5 shrink-0 rounded-full`, colorClasses)}
      />
    </div>
  )
}

const EditableScoreCell = ({
  scoreOption,
  revieweeUserId,
  revieweeName,
  existingNote,
  currentQuestionId,
  allQuestionIds,
  onScoreChangeForQuestion,
  onNoteChange,
  isSelfReview,
}: {
  scoreOption: PeerReviewMeaningfulScore | undefined
  revieweeUserId: string
  revieweeName: string
  existingNote?: string
  currentQuestionId: string
  allQuestionIds: string[]
  onScoreChangeForQuestion: (
    questionId: string,
    scoreOption: PeerReviewMeaningfulScore
  ) => void
  onNoteChange?: (revieweeUserId: string, note: string) => void
  isSelfReview: boolean
}) => {
  const [showNADialog, setShowNADialog] = useState(false)

  const handleScoreSelect = (
    selectedScoreOption: PeerReviewMeaningfulScore,
    close: () => void
  ) => {
    // If NA is selected, show the dialog instead of immediately setting it
    if (selectedScoreOption === PeerReviewMeaningfulScore.NA) {
      close()
      setShowNADialog(true)
    } else {
      onScoreChangeForQuestion(currentQuestionId, selectedScoreOption)
      close()
    }
  }

  const handleNAConfirm = (reason: string) => {
    // Set NA for all questions in this row
    for (const questionId of allQuestionIds) {
      onScoreChangeForQuestion(questionId, PeerReviewMeaningfulScore.NA)
    }
    onNoteChange?.(revieweeUserId, reason)
    setShowNADialog(false)
  }

  const handleNACancel = () => {
    setShowNADialog(false)
  }

  const isEmpty = scoreOption === undefined

  let displayContent: React.ReactNode
  if (isEmpty) {
    displayContent = (
      <div className="flex h-[1.66rem] w-[1.66rem] items-center justify-center rounded-full border-2 border-on-surface-var">
        <PlusIcon
          size={15}
          color="currentColor"
          className="text-on-surface-var"
          pathStrokeWidth={3}
        />
      </div>
    )
  } else {
    const numericalValue = peerReviewScoreProperties[scoreOption].numericalValue
    displayContent = (
      <div
        className={classNames(
          'h-5 w-5 shrink-0 rounded-full',
          peerReviewScoreColor(numericalValue)
        )}
      />
    )
  }

  return (
    <>
      {/* min-h-11 prevents layout shift due to differently sized content */}
      <div
        data-testid="peer-review-score-cell"
        data-empty={isEmpty}
        className="flex min-h-11 justify-center"
      >
        <BreakoutTooltip
          behavior="popover"
          treatReactElementAsString
          content={({ close }) => (
            <ScoreSelectionTooltipContent
              scoreOption={scoreOption}
              onScoreSelect={(selectedScoreOption) =>
                handleScoreSelect(selectedScoreOption, close)
              }
            />
          )}
        >
          <div className="flex grow cursor-pointer justify-center py-1 transition-opacity hover:opacity-80">
            <div className="flex cursor-pointer items-center justify-center p-2">
              {displayContent}
            </div>
          </div>
        </BreakoutTooltip>
      </div>
      <RequireReasonForNADialog
        open={showNADialog}
        revieweeName={revieweeName}
        existingNote={existingNote}
        onConfirm={handleNAConfirm}
        onCancel={handleNACancel}
        isSelfReview={isSelfReview}
      />
    </>
  )
}

const ScoreSelectionTooltipContent = ({
  scoreOption,
  onScoreSelect,
}: {
  scoreOption: PeerReviewMeaningfulScore | undefined
  onScoreSelect: (scoreOption: PeerReviewMeaningfulScore) => void
}) => {
  const [hoveredOption, setHoveredOption] = useState<
    PeerReviewMeaningfulScore | undefined
  >(undefined)

  const displayedOption = hoveredOption ?? scoreOption

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        {Object.values(PeerReviewMeaningfulScore).map((option) => (
          <ScoreOptionButton
            key={option}
            option={option}
            isSelected={scoreOption === option}
            onClick={() => onScoreSelect(option)}
            onMouseEnter={() => setHoveredOption(option)}
            onMouseLeave={() => setHoveredOption(undefined)}
          />
        ))}
      </div>
      <div
        className={classNames(
          'flex justify-center',
          !displayedOption && 'invisible'
        )}
        aria-hidden={!displayedOption}
      >
        {displayedOption
          ? peerReviewScoreProperties[displayedOption].label
          : 'placeholder'}
      </div>
    </div>
  )
}

const ScoreOptionButton = ({
  option,
  isSelected,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  option: PeerReviewMeaningfulScore
  isSelected: boolean
  onClick: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}) => {
  const properties = peerReviewScoreProperties[option]
  const numericalValue = properties.numericalValue

  return (
    <button
      data-testid="peer-review-score-option"
      data-score={numericalValue ?? 'na'}
      className="p-1 transition-opacity hover:opacity-60"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={classNames(
          'flex h-8 w-8 items-center justify-center rounded-full text-label-medium',
          numericalValue !== undefined && peerReviewScoreColor(numericalValue),
          numericalValue === undefined && 'ml-2 bg-on-surface-disabled',
          isSelected && 'border-2 border-white'
        )}
      >
        {option === PeerReviewMeaningfulScore.NA ? properties.label : ''}
      </div>
    </button>
  )
}

const NoteEditor = ({
  revieweeUserId,
  note,
  onNoteChange,
  isShared,
  onSharingChange,
}: {
  revieweeUserId: string
  note: string | undefined
  onNoteChange?: (revieweeUserId: string, note: string) => void
  isShared: boolean
  onSharingChange?: (revieweeUserId: string, shared: boolean) => void
}) => {
  const { tt } = useTranslationTyped()
  const [isEditing, setIsEditing] = useState(!!note)
  const currentNote = note || ''

  // Sync editing state with note prop
  useEffect(() => {
    if (note) {
      setIsEditing(true)
    }
  }, [note])

  const handleAddNote = () => {
    setIsEditing(true)
  }

  const handleNoteInput = (value: string) => {
    onNoteChange?.(revieweeUserId, value)
    // Keep editing state true if there's content or if we just started editing
    if (value || isEditing) {
      setIsEditing(true)
    }
  }

  const handleRemoveNote = () => {
    onNoteChange?.(revieweeUserId, '')
    onSharingChange?.(revieweeUserId, false)
    setIsEditing(false)
  }

  if (!isEditing && !currentNote) {
    return (
      <button
        data-testid="peer-review-add-note-button"
        onClick={handleAddNote}
        className="w-fit text-label-small text-on-surface-var underline hover:opacity-60"
      >
        {tt.student_assignment.peer_review_dialog.add_note()}
      </button>
    )
  }

  return (
    <div className="flex flex-col items-start gap-2">
      <BreakoutTextArea
        value={currentNote}
        onInput={(e) => handleNoteInput(e.currentTarget.value)}
        autoGrow
        inputClassName="!min-h-[40px] !max-h-[100px] resize-none !p-2"
        containerClassName="w-full"
        autoFocusWithDelay={isEditing && !currentNote ? 100 : undefined}
        maxLength={100}
        hideCharacterCount
      />
      <div className="flex w-full items-center justify-between">
        <BreakoutCheckbox
          label={tt.student_assignment.peer_review_dialog.share_with_peer()}
          checked={isShared}
          onChange={(checked) => onSharingChange?.(revieweeUserId, checked)}
          labelPosition="right"
          containerClassName="-ml-3"
        />
        <button
          data-testid="peer-review-remove-note-button"
          onClick={handleRemoveNote}
          className="w-fit text-label-small text-on-surface-var underline hover:opacity-60"
        >
          {tt.student_assignment.peer_review_dialog.remove_note()}
        </button>
      </div>
    </div>
  )
}
